import { createContext, useContext, ReactNode, useMemo, useEffect } from 'react';
import { useAuth } from './AuthContext.js';
import { ChatService } from '@/services/chatService.js';
import { SessionService } from '@/services/sessionService.js';
import { StreamingService } from '@/services/streamingService.js';
import { getApiUrl } from '@/lib/api-url.js';
import type { ApiClient } from '@/lib/api.js';
import { eventBus, defaultMiddleware, initializeEventHandlers, resetEventHandlers } from '@/lib/events/index.js';
// Enabled: Routines feature
import { rehydrateSchedules } from '@/lib/events/handlers.js';

interface ServicesContextType {
  chatService: ChatService;
  sessionService: SessionService;
  streamingService: StreamingService;
  apiClient: ApiClient;
}

const ServicesContext = createContext<ServicesContextType | undefined>(undefined);

export function ServicesProvider({ children }: { children: ReactNode }) {
  const { accessToken, api, user } = useAuth();

  // Initialize event bus middleware and handlers once
  useEffect(() => {
    console.log('[ServicesContext] Initializing event system');

    // Register middleware (will skip duplicates)
    defaultMiddleware.forEach((middleware) => {
      eventBus.use(middleware);
    });

    // Initialize event handlers with API client for title generation
    initializeEventHandlers(api);

    // Cleanup on unmount
    return () => {
      console.log('[ServicesContext] Cleaning up event system');
      eventBus.reset();
      resetEventHandlers();
    };
  }, [api]);

  const services = useMemo(() => {
    // Create token getter function
    const getToken = () => accessToken;
    // Create user ID getter for multi-tenant isolation
    const getUserId = () => user?.id || null;

    // StreamingService uses SSE for real-time communication
    const streamingService = new StreamingService(
      getApiUrl(),
      getToken,
      getUserId
    );
    const sessionService = new SessionService(api);
    const chatService = new ChatService(streamingService, sessionService, getToken);

    return { chatService, sessionService, streamingService, apiClient: api };
  }, [accessToken, api, user?.id]);

  // Initialize streaming service connection
  useEffect(() => {
    if (accessToken) {
      // SSE doesn't need persistent connection, but we call connect()
      // to update connection status for UI
      services.streamingService.connect().catch((error) => {
        console.error('Failed to initialize streaming service:', error);
      });
    }

    return () => {
      services.streamingService.disconnect();
    };
  }, [services.streamingService, accessToken]);

  // Enabled: Routines feature - load schedules on auth
  useEffect(() => {
    if (accessToken) {
      const timer = setTimeout(async () => {
        await rehydrateSchedules(services.apiClient);
      }, 500); // Small delay to ensure service is ready

      return () => clearTimeout(timer);
    }
  }, [accessToken, services.apiClient]);

  return (
    <ServicesContext.Provider value={services}>
      {children}
    </ServicesContext.Provider>
  );
}

export function useServices(): ServicesContextType {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error('useServices must be used within ServicesProvider');
  }
  return context;
}
